import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { ProductsContinuationApiResponse, useProducts } from 'features/products';
import { Box, Button, ButtonIcon, Flex, Section, Typography } from 'components';
import { LivesCard } from 'features/lives';
import { Link } from 'react-router-dom';
import { createUrl, request } from 'app/utils';
import { contentsTypes } from 'features/contents';
import { useSchool } from 'features/school';
import { LiveVideoCard } from 'app/types/cards/LiveVideoCard';

const ContentWrapper = styled(Flex)`
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const HatBullet = styled.i`
  position: absolute;
  display: block;
  background: ${({ theme }) => theme.colors.dark_350};
  position: absolute;
  border-radius: 50%;

  &::before {
    content: '';
    display: block;
    border: 1px solid ${({ theme }) => theme.colors.gray_600};
    border-radius: 50%;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 20px;
      height: 20px;
      left: -29px;
      top: -2px;

      &::before {
        width: 10px;
        height: 10px;
      }

      ${mediaQueries.tablet} {
        width: 26px;
        height: 26px;
        left: -52px;
        top: -4px;

        &::before {
          width: 13px;
          height: 13px;
        }
      }
    `;
  }}
`;

const ProductsContinuation = (): ReactElement => {
  const { productsContinuation, setProductsContinuation, clearProductsContinuation } = useProducts();

  const { apiGatewayUrl } = useSchool();

  const fetch = async () => {
    await request<ProductsContinuationApiResponse>({
      url: '/api/v1/School/MyNextContentForLatestAccessedCourse',
      method: 'get',
      baseURL: apiGatewayUrl,
    }).then((response) => {
      setProductsContinuation(response.data);
    });
  };

  useEffect(() => {
    fetch();

    return () => {
      clearProductsContinuation();
    };
  }, []);

  const filteredNextLives: LiveVideoCard[] = [];
  const nextLives: LiveVideoCard[] = !!productsContinuation?.nextLives?.length
    ? productsContinuation?.nextLives.reduce((prev, live) => {
        return !_.find(prev, { name: live.name, date: live.date }) ? [...prev, live] : prev;
      }, filteredNextLives)
    : undefined!;

  if (!productsContinuation || !productsContinuation.productSlug) return <></>;

  return Boolean(productsContinuation?.currentContentUuid || productsContinuation?.nextContentUuid) ? (
    <Flex flexGrow={0} bg="dark_350">
      <Section flexWrap={{ m: 'wrap', d: 'nowrap' }}>
        <Flex mr={50} flexDirection="column">
          <ContentWrapper
            flexGrow={0}
            alignItems="flex-start"
            pt={{ m: 30, t: 40, d: 80 }}
            pb={{ t: 40 }}
            pl={{ m: 20, t: 40 }}
            flexDirection="column"
          >
            <Box position="relative">
              <HatBullet />
              <Typography type="type4" color="white25" upper>
                {productsContinuation.productName}
              </Typography>
            </Box>
            <Typography type="type6" color="white" fontWeight="bold" lineHeight="1.25" mt={2}>
              {productsContinuation.currentOrNextContentName}
            </Typography>

            <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} mt={{ m: 3, t: 5 }}>
              {Boolean(productsContinuation.currentContentUuid) && (
                <Box mr={{ t: '10px' }}>
                  <Link
                    to={
                      productsContinuation.currentContentProductClassTimelineUuid
                        ? productsContinuation.productClassUuid
                          ? createUrl('contentClassDetails', [
                              productsContinuation.productSlug,
                              productsContinuation.productClassUuid,
                              productsContinuation.currentContentProductClassTimelineUuid,
                              productsContinuation.currentContentUuid,
                              contentsTypes(productsContinuation.currentContentType, productsContinuation.currentExternalDocumentType),
                            ])
                          : createUrl('contentDetails', [
                              productsContinuation.productSlug,
                              productsContinuation.currentContentProductClassTimelineUuid,
                              productsContinuation.currentContentUuid,
                              contentsTypes(productsContinuation.currentContentType, productsContinuation.currentExternalDocumentType),
                            ])
                        : productsContinuation.productClassUuid
                        ? createUrl('productContentClassDetails', [
                            productsContinuation.productSlug,
                            productsContinuation.productClassUuid,
                            '',
                            productsContinuation.currentContentUuid,
                            contentsTypes(productsContinuation.currentContentType, productsContinuation.currentExternalDocumentType),
                          ])
                        : createUrl('productContentDetails', [
                            productsContinuation.productSlug,
                            '',
                            productsContinuation.currentContentUuid,
                            contentsTypes(productsContinuation.currentContentType, productsContinuation.currentExternalDocumentType),
                          ])
                    }
                  >
                    <Button minWidth={200}>
                      {/* {productsContinuation.firstContent ? 'Iniciar' : 'Continuar'} */}
                      Continuar
                    </Button>
                  </Link>
                </Box>
              )}

              {productsContinuation.currentContentUuid !== productsContinuation.nextContentUuid && Boolean(productsContinuation.nextContentUuid) && (
                <Box mt={{ m: '10px', t: 0 }} mb={{ m: 30, t: 0 }}>
                  <Link
                    to={createUrl('contentClassDetails', [
                      productsContinuation.productSlug,
                      productsContinuation.productClassUuid,
                      productsContinuation.nextContentProductClassTimelineUuid,
                      productsContinuation.nextContentUuid,
                      contentsTypes(productsContinuation.nextContentType, productsContinuation.nextExternalDocumentType),
                    ])}
                  >
                    <Button minWidth={200} outline>
                      Próxima aula
                    </Button>
                  </Link>
                </Box>
              )}
            </Flex>
          </ContentWrapper>

          <Box pl={{ m: 20, t: 40 }} pb={{ m: 20, t: 40 }} flexGrow={0} position="relative">
            <Link to={createUrl('productClassDetails', [productsContinuation.productSlug, productsContinuation.productClassUuid])}>
              <ButtonIcon
                icon="chevronDown"
                size="large"
                position="absolute"
                left={-11}
                color="primary_400"
                bg="transparent"
                areaLabel="Ver todas as aulas"
              />

              <Typography color="primary_400" type="type1" fontWeight="bold" upper>
                Ver todas as aulas
              </Typography>
            </Link>
          </Box>
        </Flex>

        {Boolean(nextLives && nextLives.length) && (
          <Flex
            flexGrow={0}
            minWidth={{ m: 240, t: 500 }}
            flexWrap={{ m: 'wrap', d: 'nowrap' }}
            justifyContent="space-between"
            pt={{ m: '10px', t: 30, d: 40 }}
            pb={{ m: 30, t: 40, d: 40 }}
          >
            {nextLives.map((live, liveIndex) => (
              <Box key={`live-${live.uuid}-${liveIndex.toString()}`} flexGrow={0} mt={{ m: 15, t: 0 }}>
                <LivesCard live={live} productClassUuid={productsContinuation.productClassUuid} mr={2} enrolled boxSmall />
              </Box>
            ))}
          </Flex>
        )}
      </Section>
    </Flex>
  ) : (
    <></>
  );
};

export default ProductsContinuation;
